class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const loadDeferredCrossSell = () => this.loadDeferredCrossSell();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        loadDeferredCrossSell();
      },
      { once: true }
    );

    setTimeout(loadDeferredCrossSell, 220);
    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    const preservedCrossSell = this.querySelector('.cart-cross-sell');

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector, preservedCrossSell);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section', preservedCrossSell = null) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const sourceElement = parsedDocument.querySelector(selector);
    if (!sourceElement) return '';

    if (preservedCrossSell) {
      const crossSellPlaceholder = sourceElement.querySelector('cart-drawer-cross-sell');
      if (crossSellPlaceholder) {
        crossSellPlaceholder.replaceWith(preservedCrossSell.cloneNode(true));
      }
    }

    return sourceElement.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  loadDeferredCrossSell() {
    const crossSell = this.querySelector('cart-drawer-cross-sell');
    if (!crossSell || typeof crossSell.loadIfNeeded !== 'function') return;

    crossSell.loadIfNeeded();
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerCrossSell extends HTMLElement {
  connectedCallback() {
    if (this.closest('cart-drawer')?.classList.contains('active')) {
      this.loadIfNeeded();
    }
  }

  loadIfNeeded() {
    if (this.dataset.loaded === 'true' || this.dataset.loading === 'true') {
      return Promise.resolve();
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return Promise.resolve();

    this.dataset.loading = 'true';
    this.setAttribute('aria-busy', 'true');

    const requestUrl = new URL(routes.cart_url || window.location.pathname, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);

    return fetch(requestUrl.toString())
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cart drawer cross-sell request failed: ${response.status}`);
        }

        return response.text();
      })
      .then((responseText) => {
        const sectionDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const sectionElement = sectionDocument.querySelector('.shopify-section');

        if (!sectionElement || !sectionElement.innerHTML.trim()) {
          this.remove();
          return;
        }

        this.dataset.loaded = 'true';
        this.replaceWith(document.createRange().createContextualFragment(sectionElement.innerHTML));
      })
      .catch((error) => {
        delete this.dataset.loading;
        this.removeAttribute('aria-busy');
        console.error(error);
      });
  }
}

customElements.define('cart-drawer-cross-sell', CartDrawerCrossSell);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
